import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { closeOutline, openOutline } from 'ionicons/icons';
import {
  escapeHtml,
  getProductUrl,
  parseProductTokens,
  shortenProductId,
  unslugify,
} from '../utils/storefront';
import { SHOP_URL, STOREFRONT_COLLECTION_PRODUCTS_MAX } from '../constants';

const StorefrontProductIdsInput = ({
  productIds,
  productNames,
  provisionalNames,
  productUrls,
  onChange,
  onRememberLinks,
}: {
  productIds: string[];
  productNames: Record<string, string>;
  provisionalNames: Record<string, string>;
  productUrls: Record<string, string>;
  onChange: (productIds: string[]) => void;
  onRememberLinks: (
    urls: Record<string, string>,
    names: Record<string, string>,
  ) => void;
}) => {
  const { t } = useTranslation();

  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [info, setInfo] = useState<string>();

  const addProducts = () => {
    setError(undefined);
    setInfo(undefined);

    if (value.trim() === '') {
      return;
    }

    const { tokens, invalid } = parseProductTokens(value);

    // A batch is added as a whole or not at all, otherwise a single unreadable
    // link would leave the collection quietly incomplete.
    if (invalid.length !== 0) {
      setError(t('Storefront.errors.unparsableUrl', { value: invalid[0] }));

      return;
    }

    const parsed = tokens.map((token) => token.productId);
    const merged = [...new Set([...productIds, ...parsed])];
    const duplicates = productIds.length + parsed.length - merged.length;
    const capped = merged.slice(0, STOREFRONT_COLLECTION_PRODUCTS_MAX);

    if (merged.length > STOREFRONT_COLLECTION_PRODUCTS_MAX) {
      setError(
        t('Storefront.errors.limitReached', {
          max: STOREFRONT_COLLECTION_PRODUCTS_MAX,
        }),
      );
    } else if (duplicates !== 0) {
      setInfo(t('Storefront.errors.duplicates', { count: duplicates }));
    }

    const urls: Record<string, string> = {};
    const names: Record<string, string> = {};

    tokens.forEach((token) => {
      urls[token.productId] = token.raw;

      const name = token.slug ? unslugify(token.slug) : '';

      if (name !== '') {
        names[token.productId] = name;
      }
    });

    onRememberLinks(urls, names);
    onChange(capped);
    setValue('');
  };

  return (
    <div>
      <label className="text-sm font-medium">{t('Storefront.products')}</label>

      <p className="font-normal text-xs text-gray-500 leading-5 mt-1">
        {t('Storefront.productsHint')}
      </p>

      <div className="flex items-start gap-3 mt-2.5">
        <textarea
          rows={2}
          className="w-full text-sm"
          value={value}
          placeholder={`${SHOP_URL}/product/...`}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            // Enter adds a product, it must never submit the storefront form.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();

              addProducts();
            }
          }}
        />

        <button
          type="button"
          className="button py-2 px-6 text-[14px] bg-slate-900 dark:bg-slate-100 text-slate-50 dark:text-slate-800"
          onClick={addProducts}
        >
          {t('Storefront.addProducts')}
        </button>
      </div>

      {error && <p className="text-red-500 text-xs italic mt-1.5">{error}</p>}
      {info && <p className="text-xs italic mt-1.5 text-gray-500">{info}</p>}

      <div className="flex items-center justify-between mt-4">
        <span className="text-xs font-medium">
          {t('Storefront.productCount', { count: productIds.length })}
        </span>
        <span
          className={`text-xs ${
            productIds.length === STOREFRONT_COLLECTION_PRODUCTS_MAX
              ? 'text-amber-500'
              : 'text-gray-500'
          }`}
        >
          {productIds.length}/{STOREFRONT_COLLECTION_PRODUCTS_MAX}
        </span>
      </div>

      {productIds.length === 0 ? (
        <p className="text-xs italic text-gray-500 mt-2">
          {t('Storefront.noProducts')}
        </p>
      ) : (
        <ul className="mt-2 divide-y dark:divide-slate-700 border-t border-b dark:border-slate-700">
          {productIds.map((productId) => {
            const name = productNames[productId];

            // Until the api confirms a name, the one read out of the pasted
            // link stands in for it, in italics so it does not read as the real
            // name. A bare id was pasted when there is neither.
            const guess = name ? undefined : provisionalNames[productId];

            return (
              <li
                key={productId}
                className="flex items-center gap-3 py-2 text-sm"
              >
                <span
                  className={
                    name || guess
                      ? `flex-1 line-clamp-1${guess ? ' italic text-gray-500' : ''}`
                      : 'flex-1 font-mono text-xs'
                  }
                  data-uk-tooltip={`title: ${
                    guess
                      ? `${t('Storefront.guessedName')} — ${escapeHtml(productId)}`
                      : escapeHtml(productId)
                  }; pos: top-left;`}
                >
                  {name ?? guess ?? shortenProductId(productId)}
                </span>

                {/* The link is built from the name the api resolved. A product
                    added but not saved yet has none, so the pasted link stands
                    in for it. */}
                <a
                  className="flex items-center gap-1 text-xs"
                  href={
                    name
                      ? getProductUrl(productId, name)
                      : productUrls[productId] ?? getProductUrl(productId)
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('Storefront.openInShop')}
                  <IonIcon icon={openOutline} className="text-base" />
                </a>

                <button
                  type="button"
                  className="button-icon"
                  aria-label={t('Storefront.removeProduct')}
                  data-uk-tooltip={`title: ${t('Storefront.removeProduct')}; pos: top;`}
                  onClick={() =>
                    onChange(productIds.filter((id) => id !== productId))
                  }
                >
                  <IonIcon icon={closeOutline} className="text-lg" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StorefrontProductIdsInput;
